// Iter119.18 — Phase 7E usability primitives.
//
// Skeleton components for content-shaped loading placeholders.
// Used across Dashboard / Strategy Scanner V2 / Cycle Tracker so
// the operator sees a continuous layout while data is in-flight
// rather than a single centered spinner over a blank canvas.
//
// All primitives speak the ARX fortress palette — `#121216` base,
// `#26262B` border, soft gradient shimmer in `#1A1A20 → #26262B`.

import React from "react";

const BAR_BASE =
  "rounded-sm bg-gradient-to-r from-[#1A1A20] via-[#26262B] to-[#1A1A20]";

/**
 * Skeleton — single shimmering block. Pass width/height via Tailwind
 * classes (e.g. `w-24 h-4`). Defaults: full-width × text-line height.
 *
 * Usage:
 *   <Skeleton className="w-32 h-3" />
 */
export function Skeleton({ className = "", testid }) {
  return (
    <div
      aria-hidden
      data-testid={testid || "skeleton"}
      className={`${BAR_BASE} animate-skeleton-shimmer ${className || "w-full h-4"}`}
      style={{ backgroundSize: "200% 100%" }}
    />
  );
}

/**
 * SkeletonRow — horizontal stripe of N skeleton cells. Mimics a table
 * row's rough shape. `cols` defaults to 4. `align` may be "left" or
 * "right" to favour numeric (right-aligned) vs label (left-aligned)
 * placeholders.
 */
export function SkeletonRow({ cols = 4, height = "h-3", testid }) {
  return (
    <div
      data-testid={testid || "skeleton-row"}
      className="flex items-center gap-3 px-3 py-2 border-b border-[#1A1A20]"
    >
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton
          key={i}
          className={`${height} ${i === 0 ? "w-16" : "flex-1"}`}
        />
      ))}
    </div>
  );
}

/**
 * SkeletonCard — vertical card-shaped placeholder. Used to substitute
 * a TickerCard / CycleCard during the initial scan/load. Renders a
 * thick top bar (symbol) + 3 stacked text-line skeletons + a footer
 * mini-row.
 */
export function SkeletonCard({ testid, className = "" }) {
  return (
    <div
      data-testid={testid || "skeleton-card"}
      className={`bg-[#121216] border border-[#26262B] rounded-sm p-4 space-y-3 ${className}`}
    >
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="w-24 h-4" />
          <Skeleton className="w-16 h-3" />
        </div>
        <Skeleton className="w-12 h-6" />
      </div>
      <Skeleton className="w-full h-3" />
      <Skeleton className="w-3/4 h-3" />
      <div className="flex gap-2 pt-2 border-t border-[#1A1A20]">
        <Skeleton className="w-20 h-3" />
        <Skeleton className="w-16 h-3" />
        <Skeleton className="w-24 h-3" />
      </div>
    </div>
  );
}

/**
 * SkeletonList — renders N stacked SkeletonCards. Defaults to 3.
 * Used as a wholesale replacement for the centered Loader2 pattern.
 */
export function SkeletonList({ count = 3, testid }) {
  return (
    <div data-testid={testid || "skeleton-list"} className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} testid={`skeleton-card-${i}`} />
      ))}
    </div>
  );
}

export default Skeleton;
